import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { AuditTemplate, AuditTemplateQuestion, AuditTemplateSection, AuditTemplateVersion } from '../audit.models';
import { AuditService } from '../audit.service';
import { answerTypeOptions, auditNavItems, formatAuditDate, statusSeverity } from '../audit-ui';

@Component({
  selector: 'app-audit-template-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TagModule,
    TextareaModule,
    ToastModule,
    ToggleSwitchModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="Template Builder" [title]="template?.templateName || 'Audit Template'" [subtitle]="template?.templateCode || 'Draft checklist version'">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" [outlined]="true" severity="secondary"></a>
          }
        </div>
        <div actions>
          <a pButton routerLink="/audit/templates" label="Templates" icon="pi pi-arrow-left" [outlined]="true" severity="secondary"></a>
          <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary" (onClick)="load()"></p-button>
          <p-button label="Save Draft" icon="pi pi-save" (onClick)="saveDraft()" [loading]="saving" [disabled]="!canEditDraft"></p-button>
          <p-button label="Publish" icon="pi pi-check-circle" severity="success" (onClick)="publish()" [loading]="publishing" [disabled]="!canPublishDraft"></p-button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-page-data-panel title="Template Status" subtitle="Header details and active version state." [resultText]="draftVersion?.status || template?.status || '-'">
        <div class="audit-summary-grid">
          <div><span>Module</span><strong>{{ template?.moduleCode || '-' }}</strong></div>
          <div><span>Audit Type</span><strong>{{ template?.auditType || '-' }}</strong></div>
          <div><span>Language</span><strong>{{ template?.languageCode || '-' }}</strong></div>
          <div><span>Version</span><strong>{{ draftVersion?.versionNumber || '-' }}</strong></div>
          <div><span>Published At</span><strong>{{ formatDate(publishedVersion?.publishedAt) }}</strong></div>
          <div><span>Status</span><p-tag [value]="template?.status || '-'" [severity]="statusSeverity(template?.status)"></p-tag></div>
        </div>
      </app-page-data-panel>

      <app-page-data-panel title="Checklist Builder" subtitle="Sections, controls, answer types, scoring weights, and evidence rules." [resultText]="questionCount + ' questions'">
        <div actions>
          <p-button label="Add Section" icon="pi pi-plus" [outlined]="true" severity="secondary" (onClick)="addSection()" [disabled]="!canEditDraft"></p-button>
        </div>

        @if (!draftVersion) {
          <div class="audit-empty">No template version is available.</div>
        } @else if (!draftVersion.sections?.length) {
          <div class="audit-empty">Add a section to start building this checklist.</div>
        }

        @for (section of draftVersion?.sections || []; track section.sectionId || $index; let sectionIndex = $index) {
          <section class="section-card">
            <div class="section-card-header">
              <div class="section-fields">
                <p-inputgroup>
                  <p-inputgroup-addon><i class="pi pi-bookmark"></i></p-inputgroup-addon>
                  <p-iftalabel>
                    <input pInputText [id]="'section-name-' + sectionIndex" [(ngModel)]="section.sectionName" [disabled]="!canEditDraft">
                    <label [attr.for]="'section-name-' + sectionIndex">Section name</label>
                  </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                  <p-inputgroup-addon><i class="pi pi-language"></i></p-inputgroup-addon>
                  <p-iftalabel>
                    <input pInputText [id]="'section-local-name-' + sectionIndex" [(ngModel)]="section.sectionNameLocal" [disabled]="!canEditDraft">
                    <label [attr.for]="'section-local-name-' + sectionIndex">Local name</label>
                  </p-iftalabel>
                </p-inputgroup>
                <p-inputgroup>
                  <p-inputgroup-addon><i class="pi pi-sort-numeric-down"></i></p-inputgroup-addon>
                  <p-iftalabel>
                    <p-inputnumber [inputId]="'section-order-' + sectionIndex" [(ngModel)]="section.displayOrder" [showButtons]="true" [disabled]="!canEditDraft"></p-inputnumber>
                    <label [attr.for]="'section-order-' + sectionIndex">Order</label>
                  </p-iftalabel>
                </p-inputgroup>
              </div>
              <div class="section-actions">
                <p-button icon="pi pi-plus" label="Question" [outlined]="true" severity="secondary" (onClick)="addQuestion(section)" [disabled]="!canEditDraft"></p-button>
                <p-button icon="pi pi-trash" [text]="true" severity="danger" pTooltip="Remove section" (onClick)="removeSection(sectionIndex)" [disabled]="!canEditDraft"></p-button>
              </div>
            </div>

            @if (!section.questions?.length) {
              <div class="audit-empty compact">No questions in this section.</div>
            }

            @for (question of section.questions || []; track question.questionId || $index; let questionIndex = $index) {
              <div class="question-card">
                <div class="question-main">
                  <p-inputgroup class="question-text">
                    <p-inputgroup-addon><i class="pi pi-question-circle"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <textarea pTextarea rows="2" [id]="'question-text-' + sectionIndex + '-' + questionIndex" [(ngModel)]="question.questionText" [disabled]="!canEditDraft"></textarea>
                      <label [attr.for]="'question-text-' + sectionIndex + '-' + questionIndex">Question</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-code"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <input pInputText [id]="'question-control-' + sectionIndex + '-' + questionIndex" [(ngModel)]="question.controlCode" placeholder="CTRL-001" [disabled]="!canEditDraft">
                      <label [attr.for]="'question-control-' + sectionIndex + '-' + questionIndex">Control code</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-check-square"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-select [inputId]="'question-answer-type-' + sectionIndex + '-' + questionIndex" [options]="answerTypeOptions" [(ngModel)]="question.answerType" optionLabel="label" optionValue="value" [disabled]="!canEditDraft"></p-select>
                      <label [attr.for]="'question-answer-type-' + sectionIndex + '-' + questionIndex">Answer type</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-star"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-inputnumber [inputId]="'question-weight-' + sectionIndex + '-' + questionIndex" [(ngModel)]="question.weight" [min]="0" [minFractionDigits]="0" [maxFractionDigits]="2" [showButtons]="true" [disabled]="!canEditDraft"></p-inputnumber>
                      <label [attr.for]="'question-weight-' + sectionIndex + '-' + questionIndex">Weight</label>
                    </p-iftalabel>
                  </p-inputgroup>
                  <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-sort-numeric-down"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <p-inputnumber [inputId]="'question-order-' + sectionIndex + '-' + questionIndex" [(ngModel)]="question.displayOrder" [showButtons]="true" [disabled]="!canEditDraft"></p-inputnumber>
                      <label [attr.for]="'question-order-' + sectionIndex + '-' + questionIndex">Order</label>
                    </p-iftalabel>
                  </p-inputgroup>
                </div>
                <div class="question-options">
                  <label><p-toggleswitch [(ngModel)]="question.mandatory" [disabled]="!canEditDraft"></p-toggleswitch><span>Mandatory</span></label>
                  <label><p-toggleswitch [(ngModel)]="question.critical" [disabled]="!canEditDraft"></p-toggleswitch><span>Critical</span></label>
                  <label><p-toggleswitch [(ngModel)]="question.evidenceRequired" [disabled]="!canEditDraft"></p-toggleswitch><span>Evidence required</span></label>
                  <label><p-toggleswitch [(ngModel)]="question.findingOnFailure" [disabled]="!canEditDraft"></p-toggleswitch><span>Finding on failure</span></label>
                  <p-button icon="pi pi-trash" [text]="true" severity="danger" pTooltip="Remove question" (onClick)="removeQuestion(section, questionIndex)" [disabled]="!canEditDraft"></p-button>
                </div>
              </div>
            }
          </section>
        }
      </app-page-data-panel>
    </app-page-shell>
  `,
  styles: [`
    .audit-summary-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(180px, 1fr));
      gap: .85rem;
    }

    .audit-summary-grid > div {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      display: grid;
      gap: .25rem;
      min-height: 4.25rem;
      padding: .85rem;
    }

    .audit-summary-grid span,
    label span {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    .audit-summary-grid strong {
      color: #172033;
      overflow-wrap: anywhere;
    }

    .section-card {
      border: 1px solid #dfe7f3;
      border-radius: 8px;
      margin-bottom: .85rem;
      padding: .85rem;
    }

    .section-card-header {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: .85rem;
      align-items: start;
    }

    .section-fields,
    .question-main {
      display: grid;
      gap: .75rem;
    }

    .section-fields {
      grid-template-columns: minmax(220px, 1fr) minmax(180px, .8fr) 120px;
    }

    .question-main {
      grid-template-columns: minmax(260px, 1.5fr) 140px 150px 110px 110px;
    }

    label {
      color: #334155;
      display: grid;
      gap: .35rem;
      min-width: 0;
    }

    input,
    textarea,
    p-iftalabel,
    p-inputgroup,
    p-select,
    p-inputnumber {
      width: 100%;
    }

    .section-actions,
    .question-options {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: .5rem;
      justify-content: flex-end;
    }

    .question-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      margin-top: .75rem;
      padding: .75rem;
    }

    .question-options {
      border-top: 1px dashed #cbd5e1;
      justify-content: flex-start;
      margin-top: .75rem;
      padding-top: .75rem;
    }

    .question-options label {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: .45rem;
      min-height: 2rem;
    }

    .audit-empty {
      border: 1px dashed #cbd5e1;
      border-radius: 8px;
      color: #64748b;
      font-weight: 700;
      padding: 1rem;
      text-align: center;
    }

    .audit-empty.compact {
      margin-top: .75rem;
      padding: .75rem;
    }

    @media (max-width: 1100px) {
      .section-card-header,
      .section-fields,
      .question-main,
      .audit-summary-grid {
        grid-template-columns: 1fr;
      }

      .section-actions {
        justify-content: flex-start;
      }
    }
  `]
})
export class AuditTemplateDetailComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  saving = false;
  publishing = false;
  template?: AuditTemplate;
  draftVersion?: AuditTemplateVersion;
  publishedVersion?: AuditTemplateVersion;

  readonly answerTypeOptions = answerTypeOptions;
  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly canManage = this.permissionService.hasPermission(app_permissions.audit.templates.manage);
  readonly canPublish = this.permissionService.hasPermission(app_permissions.audit.templates.publish);
  readonly statusSeverity = statusSeverity;
  readonly formatDate = formatAuditDate;

  ngOnInit(): void {
    this.load();
  }

  get canEditDraft(): boolean {
    return this.canManage && this.draftVersion?.status === 'DRAFT';
  }

  get canPublishDraft(): boolean {
    return this.canPublish && this.draftVersion?.status === 'DRAFT' && this.questionCount > 0;
  }

  get questionCount(): number {
    return (this.draftVersion?.sections || [])
      .reduce((count, section) => count + (section.questions?.length || 0), 0);
  }

  get metrics(): PageMetric[] {
    const sections = this.draftVersion?.sections || [];
    const questions = sections.flatMap(section => section.questions || []);
    return [
      { label: 'Sections', value: sections.length, tone: 'info' },
      { label: 'Questions', value: questions.length, tone: 'success' },
      { label: 'Mandatory', value: questions.filter(question => question.mandatory).length, tone: 'warn' },
      { label: 'Evidence Rules', value: questions.filter(question => question.evidenceRequired).length }
    ];
  }

  load(): void {
    const templateId = Number(this.route.snapshot.paramMap.get('id'));
    this.loading = true;
    this.auditService.getTemplate(templateId).subscribe({
      next: response => {
        this.template = response.data;
        const versions = this.template?.versions || [];
        this.draftVersion = versions.find(version => version.status === 'DRAFT') || versions[0];
        this.publishedVersion = versions.find(version => version.status === 'PUBLISHED');
        if (this.draftVersion) {
          this.draftVersion.sections = this.sortSections(this.draftVersion.sections || []);
        }
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Template', detail: 'Unable to load audit template.' }),
      complete: () => this.loading = false
    });
  }

  addSection(): void {
    if (!this.draftVersion) {
      return;
    }
    const sections = this.draftVersion.sections || [];
    sections.push({
      sectionName: `Section ${sections.length + 1}`,
      displayOrder: (sections.length + 1) * 100,
      mandatory: false,
      questions: []
    });
    this.draftVersion.sections = sections;
  }

  removeSection(index: number): void {
    this.draftVersion?.sections?.splice(index, 1);
  }

  addQuestion(section: AuditTemplateSection): void {
    const questions = section.questions || [];
    questions.push({
      questionText: '',
      answerType: 'PASS_FAIL',
      weight: 1,
      mandatory: true,
      critical: false,
      evidenceRequired: false,
      findingOnFailure: true,
      displayOrder: (questions.length + 1) * 100
    });
    section.questions = questions;
  }

  removeQuestion(section: AuditTemplateSection, index: number): void {
    section.questions?.splice(index, 1);
  }

  saveDraft(): void {
    if (!this.draftVersion) {
      return;
    }
    const validation = this.validateDraft(this.draftVersion);
    if (validation) {
      this.messageService.add({ severity: 'warn', summary: 'Checklist incomplete', detail: validation });
      return;
    }
    this.saving = true;
    this.auditService.replaceTemplateVersion(this.draftVersion).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Draft saved', detail: response.message || 'Template draft saved.' });
        this.load();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Draft', detail: error?.error?.message || 'Unable to save template draft.' }),
      complete: () => this.saving = false
    });
  }

  publish(): void {
    if (!this.template?.auditTemplateId) {
      return;
    }
    this.publishing = true;
    this.auditService.publishTemplate(this.template.auditTemplateId).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Published', detail: response.message || 'Template published.' });
        this.template = response.data;
        this.load();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Publish', detail: error?.error?.message || 'Unable to publish template.' }),
      complete: () => this.publishing = false
    });
  }

  private sortSections(sections: AuditTemplateSection[]): AuditTemplateSection[] {
    return sections
      .sort((left, right) => (left.displayOrder || 0) - (right.displayOrder || 0))
      .map(section => ({
        ...section,
        questions: (section.questions || []).sort((left, right) => (left.displayOrder || 0) - (right.displayOrder || 0))
      }));
  }

  private validateDraft(version: AuditTemplateVersion): string {
    if (!version.sections?.length) {
      return 'Add at least one section.';
    }
    for (const section of version.sections) {
      if (!section.sectionName?.trim()) {
        return 'Every section needs a name.';
      }
      if (!section.questions?.length) {
        return `Add at least one question to ${section.sectionName}.`;
      }
      for (const question of section.questions) {
        if (!question.questionText?.trim()) {
          return `Every question in ${section.sectionName} needs text.`;
        }
      }
    }
    return '';
  }
}
